import { useState } from "react";
import type { SessionPlan, SessionResult } from "../data/schema";
import { Button, Screen } from "./ui";

export function SessionSummary({
  plan,
  onSave,
  onSkip,
}: {
  plan: SessionPlan;
  onSave: (difficulty: 1 | 2 | 3 | 4 | 5, notes: string, results: SessionResult[]) => void;
  onSkip: () => void;
}) {
  const [difficulty, setDifficulty] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [notes, setNotes] = useState("");
  const anchors = plan.drills
    .map((d) => d.drill)
    .filter((d) => d.anchor && d.benchmark);
  const [scores, setScores] = useState<Record<string, string>>({});

  const results: SessionResult[] = anchors
    .filter((d) => scores[d.id] !== undefined && scores[d.id] !== "")
    .map((d) => ({
      drillId: d.id,
      metric: d.benchmark!.metric,
      value: Number(scores[d.id]),
    }));

  return (
    <Screen title="Nice work">
      <p className="mb-5 text-lg text-neutral-600">Log it so we can track progress.</p>

      <h2 className="h-display mb-2 text-2xl">How hard was it?</h2>
      <div className="mb-6 flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => setDifficulty(n as 1 | 2 | 3 | 4 | 5)}
            className={`tap flex-1 rounded-2xl text-2xl font-bold ${
              difficulty === n
                ? "bg-[var(--color-pitch)] text-white"
                : "bg-white border border-[var(--color-line)]"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <p className="-mt-4 mb-6 text-sm text-neutral-500">1 = easy · 5 = maxed out</p>

      {anchors.length > 0 && (
        <div className="mb-6">
          <h2 className="h-display mb-1 text-2xl">Benchmark score</h2>
          <p className="mb-3 text-sm text-neutral-500">
            This is how we measure real improvement over time — not minutes trained.
          </p>
          {anchors.map((d) => (
            <label key={d.id} className="mb-3 block">
              <span className="mb-1 block font-semibold">{d.benchmark!.label}</span>
              <input
                type="number"
                inputMode="numeric"
                value={scores[d.id] ?? ""}
                onChange={(e) => setScores((s) => ({ ...s, [d.id]: e.target.value }))}
                className="tap w-full rounded-xl border border-[var(--color-line)] px-4 text-xl"
                placeholder="e.g. 7"
              />
            </label>
          ))}
        </div>
      )}

      <label className="mb-6 block">
        <span className="h-display mb-1 block text-2xl">Notes</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full rounded-xl border border-[var(--color-line)] p-3 text-lg"
          placeholder="How did it feel? Anything to work on?"
        />
      </label>

      <Button className="w-full" onClick={() => onSave(difficulty, notes, results)}>
        Save session
      </Button>
      <button onClick={onSkip} className="mt-3 w-full text-neutral-500">
        Skip logging
      </button>
    </Screen>
  );
}
